// region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Action dialog
//
//  Copyright:    © 2025 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
// region Version 1.0.0.0
//    001   12.03.25 Sean Flook          GMSCM-1 Initial Revision.
//    002   05.02.26 Sean Flook         GMSCM-12 Renamed and extended to handle all actions.
// endregion Version 1.0.0.0
//
//--------------------------------------------------------------------------------------------------
// endregion header

import React, { ChangeEvent, ReactElement, useEffect, useState } from "react";

import useApplicationNames from "../hooks/useApplicationNames";
import useOrganisations from "../hooks/useOrganisations";

import ApiGridType from "../models/apiGridType";
import { ApplicationDbConnectionNames, ApplicationElasticConnectionNames } from "../dtos/applicationNamesDto";
import { NewConfigDocumentDto } from "../dtos/multiTenantConfigDto";

import { isValidHttpUrl } from "../utils/HelperUtils";

import { Button, Dialog, DialogActions, DialogContent, Grid2, TextField, Typography } from "@mui/material";
import ADSDialogTitle from "../components/ADSDialogTitle";
import ADSSelectControl from "../components/ADSSelectControl";

import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";

import { blueButtonStyle, whiteButtonStyle } from "../utils/ADSStyles";
import { adsRed } from "../utils/ADSColour";
import { OrganisationNamesDto } from "../dtos/organisationsDto";

export enum DialogMode {
  "add",
  "edit",
  "delete",
}

export enum ActionVariant {
  "api",
  "namedConnectionSetting",
  "namedElasticSetting",
  "application",
  "organisation",
  "configuration",
}

interface ActionDialogProps<T = any> {
  open: boolean;
  mode: DialogMode;
  variant: ActionVariant;
  data: T;
  onDone?: (data: T) => void;
  onClose: () => void;
  onDelete?: () => void;
}

const ActionDialog: React.FC<ActionDialogProps> = ({ open, mode, variant, data, onDone, onClose, onDelete }) => {
  const { data: applicationNamesData } = useApplicationNames();
  const { data: organisationData, isLoading: organisationsLoading } = useOrganisations();

  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [actionType, setActionType] = useState<string>("unknown");
  const [dialogMaxWidth, setDialogMaxWidth] = useState<"xs" | "sm" | "md" | "lg" | "xl" | false>("xs");

  const [apiName, setApiName] = useState<string>("");
  const [apiUrl, setApiUrl] = useState<string>("");
  const [namedSetting, setNamedSetting] = useState<string>("");
  const [applicationName, setApplicationName] = useState<string>("");
  const [organisationName, setOrganisationName] = useState<string>("");

  const [apiUrlError, setApiUrlError] = useState<string | undefined>(undefined);
  const [namedSettingError, setNamedSettingError] = useState<string | undefined>(undefined);
  const [applicationNameError, setApplicationNameError] = useState<string[] | undefined>(undefined);
  const [organisationNameError, setOrganisationNameError] = useState<string[] | undefined>(undefined);

  const [organisations, setOrganisations] = useState<OrganisationNamesDto[]>([]);

  /**
   * Event to handle when the dialog is closing.
   *
   * @param {object} event The event object.
   * @param {string} reason The reason why the dialog is closing.
   */
  const handleDialogClose = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>, reason: string) => {
    event.stopPropagation();
    if (reason === "escapeKeyDown") handleCancelClick();
  };

  /**
   * Event to handle when the cancel button is clicked.
   */
  const handleCancelClick = () => {
    if (onClose) onClose();
    else setShowDialog(false);
  };

  /**
   * Event to handle when the API URL has been changed.
   *
   * @param event The change event for the TextField.
   */
  const onApiUrlChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setApiUrl(event.target.value);
  };

  /**
   * Event to handle when the named setting has been changed.
   *
   * @param event The change event for the TextField.
   */
  const onNamedSettingChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setNamedSetting(event.target.value);
  };

  /**
   * Event to handle when the application name is changed.
   * @param newValue The new application name.
   */
  const handleAppNameChangeEvent = (newValue: string): void => {
    setApplicationName(newValue);
  };

  const handleOrganisationNameChangeEvent = (newValue: string): void => {
    setOrganisationName(newValue);
  };

  /**
   * Method to determine if the data is valid or not.
   *
   * @returns {boolean} True if the data is valid; otherwise false.
   */
  const dataValid = (): boolean => {
    let validData = true;

    switch (variant) {
      case ActionVariant.api:
        if (!apiUrl) {
          setApiUrlError(`You must supply the URL for the ${apiName} API.`);
          validData = false;
        } else if (!apiUrl.startsWith("https://")) {
          setApiUrlError(`The URL must start with 'https://' for the ${apiName} API.`);
          validData = false;
        } else if (!isValidHttpUrl(apiUrl)) {
          setApiUrlError(`The URL is not valid for the ${apiName} API.`);
          validData = false;
        } else {
          setApiUrlError(undefined);
        }
        break;

      case ActionVariant.namedConnectionSetting:
      case ActionVariant.namedElasticSetting:
        if (!namedSetting || namedSetting.trim() === "") {
          setNamedSettingError("You must supply a name.");
          validData = false;
        } else if (namedSetting.includes(" ")) {
          setNamedSettingError("The name cannot contain spaces.");
          validData = false;
        } else {
          setNamedSettingError(undefined);
        }
        break;

      case ActionVariant.configuration:
        if (!applicationName || applicationName.trim() === "") {
          setApplicationNameError(["You must supply an application name."]);
          validData = false;
        } else {
          setApplicationNameError(undefined);
        }

        if (!organisationName || organisationName.trim() === "") {
          setOrganisationNameError(["You must supply an organisation name."]);
          validData = false;
        } else {
          setOrganisationNameError(undefined);
        }
        break;

      default:
        break;
    }

    return validData;
  };

  /**
   * Method to determine if the data has been changed or not.
   *
   * @returns {boolean} True if the data has been changed; otherwise false.
   */
  const hasDataChanged = (): boolean => {
    let dataChanged = false;

    switch (variant) {
      case ActionVariant.api:
        const oldApiRecord = data as ApiGridType;
        dataChanged = oldApiRecord.name !== apiName || oldApiRecord.url !== apiUrl;
        break;

      case ActionVariant.namedConnectionSetting:
      case ActionVariant.namedElasticSetting:
        const oldNamedSettingRecord = data as string;
        dataChanged = oldNamedSettingRecord !== namedSetting;
        break;

      case ActionVariant.configuration:
        const oldDocumentRecord = data as NewConfigDocumentDto;
        dataChanged =
          oldDocumentRecord.appName !== applicationName || oldDocumentRecord.organisation !== organisationName;
        break;

      default:
        break;
    }

    return dataChanged;
  };

  /**
   * Method to get the returned data.
   *
   * @returns {object} The returned data object.
   */
  const getReturnData = (): any => {
    switch (variant) {
      case ActionVariant.api:
        return { id: (data as ApiGridType).id, name: apiName, url: apiUrl };

      case ActionVariant.namedConnectionSetting:
        return { id: (data as ApplicationDbConnectionNames).id, name: namedSetting };

      case ActionVariant.namedElasticSetting:
        return { id: (data as ApplicationElasticConnectionNames).id, name: namedSetting };

      case ActionVariant.configuration:
        return { appName: applicationName, organisation: organisationName };

      default:
        break;
    }
  };

  /**
   * Event to handle when the done button is clicked.
   */
  const handleDoneClick = () => {
    if (dataValid()) {
      if (hasDataChanged()) {
        if (onDone) onDone(getReturnData());
      } else handleCancelClick();
    }
  };

  /**
   * Event to handle when the delete button is clicked.
   */
  const handleDeleteClick = () => {
    if (onDelete) onDelete();
  };

  /**
   * Method to get the dialog title depending on the current variant.
   *
   * @returns {string} The dialog title.
   */
  const getByTitle = (): string => {
    let actionMode: string = "";
    switch (mode) {
      case DialogMode.add:
        actionMode = "Add";
        break;

      case DialogMode.edit:
        actionMode = "Edit";
        break;

      case DialogMode.delete:
        actionMode = "Delete";
        break;

      default:
        actionMode = "Unknown";
        break;
    }

    return `${actionMode} ${actionType}`;
  };

  /**
   * Method to get the dialog content depending on the current variant.
   *
   * @returns {ReactElement} The JSX used for the dialog content.
   */
  const getContent = (): ReactElement => {
    switch (variant) {
      case ActionVariant.api:
        return (
          <Grid2 container alignItems="center" rowSpacing={2}>
            <Grid2 size={2}>
              <Typography variant="body1" align="right" gutterBottom>
                URL
              </Typography>
            </Grid2>
            <Grid2 size={10}>
              <TextField
                variant="outlined"
                error={!!apiUrlError}
                helperText={
                  <Typography variant="caption" sx={{ color: adsRed }} align="left">
                    {apiUrlError}
                  </Typography>
                }
                value={apiUrl ?? ""}
                fullWidth
                size="small"
                sx={(theme) => ({
                  pl: theme.spacing(1),
                  pr: theme.spacing(1),
                })}
                onChange={onApiUrlChange}
                slotProps={{
                  htmlInput: { maxLength: "100" },
                }}
              />
            </Grid2>
          </Grid2>
        );

      case ActionVariant.namedConnectionSetting:
        if (mode === DialogMode.delete) {
          return (
            <>
              <Typography variant="body2" align="center" sx={{ color: adsRed }}>
                Are you sure you want to delete this named connection setting?
              </Typography>
              <Typography variant="body1" align="center" sx={{ fontWeight: 600, mt: 2 }}>
                {namedSetting}
              </Typography>
            </>
          );
        } else {
          return (
            <Grid2 container alignItems="center" rowSpacing={2}>
              <Grid2 size={2}>
                <Typography variant="body1" align="right" gutterBottom>
                  Name
                </Typography>
              </Grid2>
              <Grid2 size={10}>
                <TextField
                  variant="outlined"
                  error={!!namedSettingError}
                  helperText={
                    <Typography variant="caption" sx={{ color: adsRed }} align="left">
                      {namedSettingError}
                    </Typography>
                  }
                  value={namedSetting ?? ""}
                  fullWidth
                  size="small"
                  sx={(theme) => ({
                    pl: theme.spacing(1),
                    pr: theme.spacing(1),
                  })}
                  onChange={onNamedSettingChange}
                  slotProps={{
                    htmlInput: { maxLength: "100" },
                  }}
                />
              </Grid2>
            </Grid2>
          );
        }

      case ActionVariant.namedElasticSetting:
        if (mode === DialogMode.delete) {
          return (
            <>
              <Typography variant="body2" align="center" sx={{ color: adsRed }}>
                Are you sure you want to delete this named elastic setting?
              </Typography>
              <Typography variant="body1" align="center" sx={{ fontWeight: 600, mt: 2 }}>
                {namedSetting}
              </Typography>
            </>
          );
        } else {
          return (
            <Grid2 container alignItems="center" rowSpacing={2}>
              <Grid2 size={2}>
                <Typography variant="body1" align="right" gutterBottom>
                  Name
                </Typography>
              </Grid2>
              <Grid2 size={10}>
                <TextField
                  variant="outlined"
                  error={!!namedSettingError}
                  helperText={
                    <Typography variant="caption" sx={{ color: adsRed }} align="left">
                      {namedSettingError}
                    </Typography>
                  }
                  value={namedSetting ?? ""}
                  fullWidth
                  size="small"
                  sx={(theme) => ({
                    pl: theme.spacing(1),
                    pr: theme.spacing(1),
                  })}
                  onChange={onNamedSettingChange}
                  slotProps={{
                    htmlInput: { maxLength: "100" },
                  }}
                />
              </Grid2>
            </Grid2>
          );
        }

      case ActionVariant.application:
        return (
          <>
            <Typography variant="body2" align="center" sx={{ color: adsRed }}>
              Are you sure you want to delete this application?
            </Typography>
            <Typography variant="body1" align="center" sx={{ fontWeight: 600, mt: 2 }}>
              {applicationName}
            </Typography>
          </>
        );

      case ActionVariant.organisation:
        return (
          <>
            <Typography variant="body2" align="center" sx={{ color: adsRed }}>
              Are you sure you want to delete this organisation?
            </Typography>
            <Typography variant="body1" align="center" sx={{ fontWeight: 600, mt: 2 }}>
              {organisationName}
            </Typography>
          </>
        );

      case ActionVariant.configuration:
        return (
          <>
            <ADSSelectControl
              label="Application"
              value={applicationName}
              isEditable
              lookupData={applicationNamesData || []}
              lookupId="name"
              lookupLabel="name"
              isRequired
              helperText="The application name of the document."
              errorText={applicationNameError}
              onChange={(id) => handleAppNameChangeEvent(id?.toString() || "")}
            />
            <ADSSelectControl
              label="Organisation"
              value={organisationName}
              isEditable
              lookupData={organisations}
              lookupId="name"
              lookupLabel="name"
              isRequired
              helperText="The name of the organisation."
              errorText={organisationNameError}
              onChange={(id) => handleOrganisationNameChangeEvent(id?.toString() || "")}
            />
          </>
        );

      default:
        return <></>;
    }
  };

  const getActions = (): ReactElement => {
    switch (mode) {
      case DialogMode.delete:
        return (
          <>
            <Button
              onClick={handleDeleteClick}
              autoFocus
              variant="contained"
              sx={blueButtonStyle}
              startIcon={<DeleteForeverOutlinedIcon />}
            >
              Delete
            </Button>
            <Button onClick={handleCancelClick} variant="contained" sx={whiteButtonStyle} startIcon={<CloseIcon />}>
              Cancel
            </Button>
          </>
        );
      default:
        return (
          <>
            <Button
              onClick={handleDoneClick}
              autoFocus
              variant="contained"
              sx={blueButtonStyle}
              startIcon={<DoneIcon />}
            >
              Done
            </Button>
            <Button onClick={handleCancelClick} variant="contained" sx={whiteButtonStyle} startIcon={<CloseIcon />}>
              Cancel
            </Button>
          </>
        );
    }
  };

  useEffect(() => {
    switch (variant) {
      case ActionVariant.api:
        setActionType(`${(data as ApiGridType)?.name} API`);
        setApiName((data as ApiGridType)?.name ?? "");
        setApiUrl((data as ApiGridType)?.url ?? "");
        setDialogMaxWidth("md");
        break;

      case ActionVariant.namedConnectionSetting:
        setActionType("connection setting name");
        setNamedSetting((data as ApplicationDbConnectionNames)?.name ?? "");
        setDialogMaxWidth("sm");
        break;

      case ActionVariant.namedElasticSetting:
        setActionType("elastic setting name");
        setNamedSetting((data as ApplicationElasticConnectionNames)?.name ?? "");
        setDialogMaxWidth("sm");
        break;

      case ActionVariant.application:
        setActionType("application name");
        setApplicationName((data as string) ?? "");
        setDialogMaxWidth("sm");
        break;

      case ActionVariant.organisation:
        setActionType("organisation name");
        setOrganisationName((data as string) ?? "");
        setDialogMaxWidth("sm");
        break;

      case ActionVariant.configuration:
        setActionType("configuration document");
        setApplicationName((data as NewConfigDocumentDto).appName ?? "");
        setOrganisationName((data as NewConfigDocumentDto).organisation ?? "");
        setDialogMaxWidth("sm");
        break;

      default:
        setActionType("unknown");
        break;
    }
  }, [variant, data]);

  useEffect(() => {
    setShowDialog(open);
  }, [open]);

  useEffect(() => {
    if (!organisationsLoading) {
      const mappedOrganisations =
        organisationData && organisationData.length > 0
          ? organisationData
              .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }))
              .map((x) => ({ name: x }))
          : [];
      setOrganisations(mappedOrganisations);
    }
  }, [organisationData, organisationsLoading]);

  return (
    <Dialog
      open={showDialog}
      aria-labelledby="edit-dialog"
      fullWidth
      maxWidth={dialogMaxWidth}
      onClose={handleDialogClose}
    >
      <ADSDialogTitle title={getByTitle()} closeTooltip="Cancel" onClose={handleCancelClick} />
      <DialogContent sx={(theme) => ({ mt: theme.spacing(2) })}>{getContent()}</DialogContent>
      <DialogActions sx={(theme) => ({ justifyContent: "flex-start", mb: theme.spacing(1), ml: theme.spacing(3) })}>
        {getActions()}
      </DialogActions>
    </Dialog>
  );
};

export default ActionDialog;
